import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Upload, Wand2, Link2, Unlink, EyeOff, Eye, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { PageHeader, Panel, Loading, Empty, Field, Th, Td, TableWrap, Money, NativeSelect, Kpi } from "@/components/kit";
import { dmy, errMsg } from "@/lib/format";

const MAP_FIELDS = ["date", "label", "amount", "debit", "credit", "reference"];

const ImportDialog = ({ open, onClose, onDone }) => {
  const { api, t } = useApp();
  const ref = useRef();
  const [file, setFile] = useState(null);
  const [pv, setPv] = useState(null);
  const [map, setMap] = useState({});
  const [bank, setBank] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!open) { setFile(null); setPv(null); setMap({}); } }, [open]);
  const pick = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await api.post("/bank/preview", fd); setFile(f); setPv(data); setMap(data.mapping || {}); } catch (err) { toast.error(errMsg(err)); }
    e.target.value = "";
  };
  const go = async () => {
    setBusy(true);
    const fd = new FormData(); fd.append("file", file); fd.append("mapping", JSON.stringify(map)); fd.append("bank_name", bank);
    try {
      const { data } = await api.post("/bank/import", fd);
      toast.success(`${data.imported} ${t("bank_imported")} · ${data.matched} ${t("bank_auto_matched")}${data.duplicates ? ` · ${data.duplicates} ${t("bank_dups")}` : ""}`);
      data.errors.slice(0, 3).forEach((x) => toast.warning(x));
      onDone(); onClose();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{t("bank_import")}</DialogTitle></DialogHeader>
        <input ref={ref} type="file" accept=".csv,.xlsx,.ofx,.qfx" hidden onChange={pick} data-testid="bank-file-input" />
        <button onClick={() => ref.current.click()} className="w-full rounded-xl border-2 border-dashed border-slate-200 p-6 text-sm text-slate-500 hover:border-red-300 hover:bg-red-50/40" data-testid="bank-file-btn">
          <Upload className="mx-auto mb-2 h-5 w-5" />{file ? file.name : t("bank_drop")}
        </button>
        {pv && (
          <div className="space-y-3" data-testid="bank-preview">
            <p className="text-xs text-slate-500">{pv.count} {t("lines").toLowerCase()} · {pv.format === "ofx" ? "OFX" : "CSV / Excel"}</p>
            <Field label={t("bank")}><Input value={bank} onChange={(e) => setBank(e.target.value)} placeholder="BIAT, STB, Attijari…" data-testid="bank-name" /></Field>
            {pv.format === "table" && (
              <div className="grid gap-2 sm:grid-cols-3">
                {MAP_FIELDS.map((k) => (
                  <Field key={k} label={t(`bank_col_${k}`)}>
                    <NativeSelect value={map[k] || ""} onChange={(e) => setMap({ ...map, [k]: e.target.value })} data-testid={`bank-map-${k}`}>
                      <option value="">—</option>{pv.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                    </NativeSelect>
                  </Field>
                ))}
              </div>
            )}
            <p className="text-[11px] text-slate-400">{t("bank_map_help")}</p>
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>{t("cancel")}</Button><Button onClick={go} disabled={!pv || busy} data-testid="bank-import-submit">{t("import")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const MatchDialog = ({ tx, onClose, onDone }) => {
  const { api, t } = useApp();
  const [c, setC] = useState(null);
  useEffect(() => { if (tx) api.get(`/bank/transactions/${tx.id}/candidates`).then((r) => setC(r.data)); else setC(null); }, [tx, api]);
  const run = async (fn) => { try { await fn(); toast.success(t("bank_matched")); onDone(); onClose(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Dialog open={!!tx} onOpenChange={onClose}>
      <DialogContent className="max-w-xl bg-white">
        <DialogHeader><DialogTitle>{t("bank_match")}</DialogTitle></DialogHeader>
        {tx && <p className="text-sm text-slate-600">{dmy(tx.date)} · {tx.label} · <Money v={tx.amount} /></p>}
        {!c ? <Loading /> : (
          <div className="space-y-4">
            <div><div className="eyebrow mb-1">{t("bank_existing_payments")}</div>
              {c.payments.length === 0 ? <p className="text-xs text-slate-400">{t("empty")}</p> : c.payments.map((p) => (
                <div key={p.id} className="flex items-center gap-2 border-b border-slate-50 py-2 text-sm">
                  <span className="num">{p.number}</span><span className="text-slate-500">{dmy(p.date)} · {p.partner_name}</span>
                  <Button size="sm" className="ms-auto" onClick={() => run(() => api.post(`/bank/transactions/${tx.id}/match`, { payment_id: p.id }))} data-testid={`bank-match-pay-${p.number}`}>{t("bank_link")}</Button>
                </div>))}
            </div>
            <div><div className="eyebrow mb-1">{t("bank_open_docs")}</div>
              {c.documents.length === 0 ? <p className="text-xs text-slate-400">{t("empty")}</p> : c.documents.map((d) => (
                <div key={d.document_id} className="flex items-center gap-2 border-b border-slate-50 py-2 text-sm">
                  <span className="num">{d.number}</span><span className="text-slate-500">{d.partner_name}</span>
                  <Button size="sm" variant="outline" className="ms-auto" onClick={() => run(() => api.post(`/bank/transactions/${tx.id}/settle`, { document_id: d.document_id }))} data-testid={`bank-settle-doc-${d.number}`}><Plus className="h-3.5 w-3.5 me-1" />{t("bank_settle")}</Button>
                </div>))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const STATUS_CLS = { matched: "bg-emerald-50 text-emerald-700 border-emerald-200", unmatched: "bg-amber-50 text-amber-800 border-amber-200", ignored: "bg-slate-100 text-slate-500 border-slate-200" };

const TxRow = ({ x, rw, act, setMatch }) => {
  const { t } = useApp();
  const sug = x.suggestions?.[0];
  return (
    <tr className="border-b border-slate-50" data-testid={`bank-row-${x.id}`}>
      <Td className="num text-xs">{dmy(x.date)}</Td>
      <Td className="max-w-[320px]"><div className="truncate">{x.label}</div>{x.reference && <div className="num text-[11px] text-slate-400">{x.reference}</div>}</Td>
      <Td className={`text-end ${x.amount < 0 ? "text-red-600" : "text-emerald-700"}`}><Money v={x.amount} /></Td>
      <Td><span className={`whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-semibold ${STATUS_CLS[x.status]}`} data-testid={`bank-status-${x.id}`}>{t(`bank_st_${x.status}`)}</span></Td>
      <Td className="text-xs">{x.payment_number ? <span className="num">{x.payment_number} <span className="text-slate-400">· {x.partner_name}</span></span> : sug ? <span className="text-slate-500">{t("bank_suggest")} : <b className="num">{sug.number}</b> {sug.partner_name}</span> : "—"}</Td>
      <Td className="text-end whitespace-nowrap">{rw && (x.status === "matched" ? (
        <Button size="sm" variant="ghost" onClick={() => act(`/bank/transactions/${x.id}/unmatch`)} data-testid={`bank-unmatch-${x.id}`}><Unlink className="h-4 w-4" /></Button>
      ) : (<>
        {x.status === "unmatched" && sug && <Button size="sm" variant="outline" onClick={() => act(`/bank/transactions/${x.id}/settle`, { document_id: sug.document_id })} data-testid={`bank-settle-${x.id}`}><Plus className="h-3.5 w-3.5 me-1" />{t("bank_settle")}</Button>}
        {x.status === "unmatched" && <Button size="sm" variant="ghost" onClick={() => setMatch(x)} data-testid={`bank-match-${x.id}`}><Link2 className="h-4 w-4" /></Button>}
        <Button size="sm" variant="ghost" onClick={() => act(`/bank/transactions/${x.id}/ignore`)} data-testid={`bank-ignore-${x.id}`}>{x.status === "ignored" ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}</Button>
      </>))}</Td>
    </tr>
  );
};

export default function Bank() {
  const { api, t, can } = useApp();
  const [tab, setTab] = useState("unmatched");
  const [data, setData] = useState(null);
  const [stmts, setStmts] = useState([]);
  const [imp, setImp] = useState(false);
  const [match, setMatch] = useState(null);
  const rw = can("bank", true);
  const load = useCallback(() => {
    api.get("/bank/transactions", { params: { status: tab === "all" ? undefined : tab } }).then((r) => setData(r.data));
    api.get("/bank/statements").then((r) => setStmts(r.data));
  }, [api, tab]);
  useEffect(() => { load(); }, [load]);
  const act = async (url, body = {}) => { try { await api.post(url, body); load(); } catch (e) { toast.error(errMsg(e)); } };
  const auto = async () => { try { const { data: d } = await api.post("/bank/auto-match"); toast.success(`${d.matched} ${t("bank_auto_matched")}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const delStmt = async (s) => { if (window.confirm(t("delete"))) { try { await api.delete(`/bank/statements/${s.id}`); load(); } catch (e) { toast.error(errMsg(e)); } } };
  const sm = data?.summary || {};
  return (
    <>
      <PageHeader eyebrow={t("payments")} title={t("bank_rec")}>
        {rw && <Button variant="outline" onClick={auto} data-testid="bank-auto-match"><Wand2 className="h-4 w-4 me-1" />{t("bank_auto")}</Button>}
        {rw && <Button onClick={() => setImp(true)} data-testid="bank-import-btn"><Upload className="h-4 w-4 me-1" />{t("bank_import")}</Button>}
      </PageHeader>
      <div className="grid gap-4 sm:grid-cols-3">
        <Kpi label={t("bank_st_matched")} value={sm.matched?.count || 0} sub={<Money v={sm.matched?.amount || 0} />} testid="bank-kpi-matched" />
        <Kpi label={t("bank_st_unmatched")} value={sm.unmatched?.count || 0} sub={<Money v={sm.unmatched?.amount || 0} />} accent="orange" testid="bank-kpi-unmatched" />
        <Kpi label={t("bank_st_ignored")} value={sm.ignored?.count || 0} sub={<Money v={sm.ignored?.amount || 0} />} accent="navy" testid="bank-kpi-ignored" />
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border">{["unmatched", "matched", "ignored", "all"].map((k) => <TabsTrigger key={k} value={k} data-testid={`bank-tab-${k}`}>{k === "all" ? t("all") : t(`bank_st_${k}`)}</TabsTrigger>)}</TabsList>
      </Tabs>
      <Panel>
        {!data ? <Loading /> : data.rows.length === 0 ? <Empty text={t("bank_empty")} /> : (
          <TableWrap>
            <thead className="border-b"><tr><Th>{t("date")}</Th><Th>{t("label")}</Th><Th className="text-end">{t("amount")}</Th><Th>{t("status")}</Th><Th>{t("bank_payment")}</Th><Th /></tr></thead>
            <tbody>{data.rows.map((x) => <TxRow key={x.id} x={x} rw={rw} act={act} setMatch={setMatch} />)}</tbody>
          </TableWrap>
        )}
      </Panel>
      {stmts.length > 0 && (
        <Panel title={t("bank_statements")} testid="bank-statements">
          {stmts.map((s) => (
            <div key={s.id} className="flex items-center gap-3 border-b border-slate-50 py-2 text-sm">
              <span className="font-medium">{s.bank_name || s.filename}</span><span className="text-xs text-slate-500">{dmy(s.from)} → {dmy(s.to)} · {s.count} op.</span>
              {rw && <Button size="sm" variant="ghost" className="ms-auto" onClick={() => delStmt(s)} data-testid={`bank-stmt-delete-${s.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>}
            </div>
          ))}
          <p className="mt-2 text-xs text-slate-400">{t("bank_help")} <Link to="/app/payments" className="underline">{t("payments")}</Link></p>
        </Panel>
      )}
      <ImportDialog open={imp} onClose={() => setImp(false)} onDone={load} />
      <MatchDialog tx={match} onClose={() => setMatch(null)} onDone={load} />
    </>
  );
}
